import { EntityArgTypes, SyncableEntityType } from "@/stores/sync-store";

interface ArtefactOPFSFileConstructorOptions<T extends SyncableEntityType> {
    info: ArtefactOPFSIdentity<T>;
    handle: FileSystemFileHandle;
    root: FileSystemDirectoryHandle;
    writable: WritableStreamDefaultWriter | null;
}

/**
 * Extended identity for an artefact in OPFS which includes metadata.
 */
export interface ArtefactOPFSIdentity<T extends SyncableEntityType> {
    organizationId: string;
    entityType: T;
    entityId: string;
    attribute: Extract<keyof EntityArgTypes[T], string>;
    mimetype: string;
}

/**
 * Only used for hashing purposes as the string length is limited.
 */
interface TinyArtefactOPFSIdentity<T extends SyncableEntityType> {
    o: ArtefactOPFSIdentity<T>["organizationId"];
    e: ArtefactOPFSIdentity<T>["entityType"];
    i: ArtefactOPFSIdentity<T>["entityId"];
    a: ArtefactOPFSIdentity<T>["attribute"];
    m: ArtefactOPFSIdentity<T>["mimetype"];
}

export class ArtefactOPFSFile<T extends SyncableEntityType> {
    handle: FileSystemFileHandle | null = null;
    info: ArtefactOPFSIdentity<T>;
    root: FileSystemDirectoryHandle;
    private writable: WritableStreamDefaultWriter | null = null;

    private constructor(options: ArtefactOPFSFileConstructorOptions<T>) {
        this.info = options.info;
        this.handle = options.handle;
        this.root = options.root;
        this.writable = options.writable;
    }

    /*
     * Closes the ArtefactOPFSFile if it's not already closed
     */
    async close() {
        await this.writable?.ready;
        await this.writable?.close();
    }

    /*
     * Removes the ArtefactOPFSFile from the filesystem if it exists
     */
    async remove() {
        const identity = ArtefactOPFSFile.getIdentityHash(this.info);
        try {
            this.writable?.releaseLock();
        } catch {
            // Ignore if the writable is already closed.
        }
        try {
            await this.root?.removeEntry(identity);
        } catch {
            // Ignore if the entity is already removed.
        }
    }

    /*
     * Write the file and close it.
     */
    async write(content: Blob) {
        if (this.writable !== null) {
            await this.writable.closed;
        }

        if (!this.handle) {
            throw new Error("Cannot write to a closed file");
        }

        this.writable = (await this.handle.createWritable()).getWriter();
        await this.writable.write(content);
        await this.close();
    }

    /*
     * Gets a ArtefactOPFSFile for a specific artefact
     * If the file does not exist, it will be created
     * @param artefact - the Artefact associated with the ArtefactOPFSFile
     * @param isWritable - whether the ArtefactOPFSFile should be writable
     */
    static async getOrCreate<T extends SyncableEntityType>(
        info: ArtefactOPFSIdentity<T>
    ) {
        const opfsRoot = await navigator.storage.getDirectory();
        const identity = ArtefactOPFSFile.getIdentityHash(info);
        let handle: FileSystemFileHandle | null = null;
        try {
            handle = await opfsRoot.getFileHandle(identity, {});
        } catch {
            // Continue to create new file below.
        }
        if (!handle) {
            handle = await opfsRoot.getFileHandle(identity, {
                create: true,
            });
        }

        return new ArtefactOPFSFile({
            info,
            handle,
            root: opfsRoot,
            writable: null,
        });
    }

    /*
     * Gets a ArtefactOPFSFile for a specific artefact
     * If the file does not exist, it will return null
     * @param artefact - the Artefact associated with the ArtefactOPFSFile
     * @param isWritable - whether the ArtefactOPFSFile should be writable
     */
    static async get<T extends SyncableEntityType>(
        info: ArtefactOPFSIdentity<T>,
        _opfsRoot?: FileSystemDirectoryHandle
    ): Promise<ArtefactOPFSFile<T> | undefined> {
        const opfsRoot = _opfsRoot || (await navigator.storage.getDirectory());
        const identity = ArtefactOPFSFile.getIdentityHash(info);

        let handle: FileSystemFileHandle | null = null;
        try {
            handle = await opfsRoot.getFileHandle(identity, {});
            // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (e) {
            // Continue to return null below.
        }
        if (!handle) {
            return undefined;
        }

        return new ArtefactOPFSFile({
            info,
            handle,
            root: opfsRoot,
            writable: null,
        });
    }

    static async getEntryIdentitiesForOrganization(
        organizationId: string
    ): Promise<ArtefactOPFSIdentity<SyncableEntityType>[]> {
        const opfsRoot = await navigator.storage.getDirectory();
        const identities: ArtefactOPFSIdentity<SyncableEntityType>[] = [];
        for await (const key of opfsRoot.keys()) {
            try {
                const identity = ArtefactOPFSFile.decodeIdentityHash(key);
                if (identity.organizationId === organizationId) {
                    identities.push(identity);
                }
            } catch {
                // Incorrectly hashed, so precedes the autosave era.
                continue;
            }
        }

        return identities;
    }

    /*
     * Checks if a ArtefactOPFSFile exists for a specific artefact
     * @param artefact - the Artefact associated with the ArtefactOPFSFile
     */
    static async exists<T extends SyncableEntityType>(
        info: ArtefactOPFSIdentity<T>
    ) {
        const opfsRoot = await navigator.storage.getDirectory();
        const identity = ArtefactOPFSFile.getIdentityHash(info);
        try {
            await opfsRoot.getFileHandle(identity, {});
            return true;
            // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (e) {
            return false;
        }
    }

    /**
     * Base64 encode the JSON and then encode as URI component so no
     * invalid characters can exist.
     */
    static getIdentityHash<T extends SyncableEntityType>(
        info: ArtefactOPFSIdentity<T>
    ): string {
        const tinyInfo: TinyArtefactOPFSIdentity<T> = {
            o: info.organizationId,
            e: info.entityType,
            i: info.entityId,
            a: info.attribute,
            m: info.mimetype,
        };
        return encodeURIComponent(
            btoa(JSON.stringify(tinyInfo).replace(/\s+/, ""))
        );
    }

    /**
     * Do the reverse of `getIdentityHash`.
     *
     * @throws {Error} If the hash isn't encoded with `getIdentityHash`.
     */
    static decodeIdentityHash<T extends SyncableEntityType>(
        hash: string
    ): ArtefactOPFSIdentity<T> {
        const tinyInfo = JSON.parse(
            atob(decodeURIComponent(hash))
        ) as TinyArtefactOPFSIdentity<T>;
        return {
            organizationId: tinyInfo.o,
            entityType: tinyInfo.e,
            entityId: tinyInfo.i,
            attribute: tinyInfo.a,
            mimetype: tinyInfo.m,
        };
    }
}
